import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type AbuseReport, type ReportAction } from '@/lib/api'

type ReportFilter = 'open' | 'resolved' | 'all'

export function ReportsPage() {
  const [filter, setFilter] = useState<ReportFilter>('open')
  const queryClient = useQueryClient()

  const { data: reports, isLoading } = useQuery({
    queryKey: ['reports', filter],
    queryFn: () => adminApi.reports.list({ status: filter }),
  })

  const { data: warrantCanary } = useQuery({
    queryKey: ['warrant-canary'],
    queryFn: () => adminApi.reports.getWarrantCanaryStatus(),
  })

  const actionMutation = useMutation({
    mutationFn: ({
      reportId,
      action,
    }: {
      reportId: string
      action: ReportAction
    }) => adminApi.reports.takeAction(reportId, action),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] })
    },
  })

  function handleReportAction(reportId: string, action: ReportAction) {
    actionMutation.mutate({ reportId, action })
  }

  function getSeverityClasses(severity: string): string {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  return (
    <div className="px-8 py-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage abuse reports and monitor the warrant canary status.
        </p>
      </div>

      {/* Warrant Canary Status */}
      <div className="mb-8 rounded-lg border-2 border-dashed border-gray-300 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Warrant Canary
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Transparency statement confirming no government data requests
              have been received.
            </p>
          </div>
          {warrantCanary && (
            <div className="text-right">
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${
                  warrantCanary.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {warrantCanary.isActive ? 'Active' : 'Inactive'}
              </span>
              <p className="mt-1 text-xs text-gray-500">
                Last updated:{' '}
                {new Date(warrantCanary.lastUpdated).toLocaleDateString()}
              </p>
              <p className="text-xs text-gray-400">
                Next scheduled update:{' '}
                {new Date(warrantCanary.nextUpdate).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="mb-6 flex space-x-1 rounded-lg bg-gray-100 p-1">
        {(['open', 'resolved', 'all'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Reports list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
        </div>
      ) : reports && reports.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">
          No reports to display.
        </div>
      ) : (
        <div className="space-y-4">
          {reports?.map((report: AbuseReport) => (
            <div
              key={report.id}
              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getSeverityClasses(report.severity)}`}
                    >
                      {report.severity.toUpperCase()}
                    </span>
                    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                      {report.category}
                    </span>
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        report.status === 'open'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {report.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">
                    {report.description}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>Report ID: {report.id}</span>
                    <span>Reported by: {report.reporterUsername}</span>
                    <span>Target: {report.targetUsername}</span>
                    <span>
                      Filed:{' '}
                      {new Date(report.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {report.status === 'open' && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        handleReportAction(report.id, 'acknowledge')
                      }
                      disabled={actionMutation.isPending}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
                    >
                      Acknowledge
                    </button>
                    <button
                      onClick={() =>
                        handleReportAction(report.id, 'escalate')
                      }
                      disabled={actionMutation.isPending}
                      className="rounded-md border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 shadow-sm transition-colors hover:bg-orange-100 disabled:opacity-50"
                    >
                      Escalate
                    </button>
                    <button
                      onClick={() =>
                        handleReportAction(report.id, 'resolve')
                      }
                      disabled={actionMutation.isPending}
                      className="rounded-md border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 shadow-sm transition-colors hover:bg-green-100 disabled:opacity-50"
                    >
                      Resolve
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
